import React, { useState, useCallback, useEffect } from 'react';
import {
  Grid, Stack, Box, FormControlLabel,
} from '@mui/material';
import PropTypes from 'prop-types';
import {
  useStyles,
  CardSubtitleTypography,
  CardLabelTypography,
  StyledCheckBox,
  AllocationLableTypography,
} from './perFundAllocationStrategyCardStyle';
import { getFundVintagesFromCache } from '../../../../../shared/services/apiGateway';
import ToggleIconWithInput from './ToggleIconWithInput';

export default function PerFundAllocationStrategyCard({
  logo,
  title,
  fundFamilysubtitle,
  subtitle,
  formData,
  setFormData,
  fund,
  autoAnnualReinvestmentLabel,
  autoDistributionReinvestmentLabel,
  onUpdateAllocation,
  fundData,
  fundFamilies,
  useLightBackground,
}) {
  const classes = useStyles();
  const [minimumAmount, setMinimumAmount] = useState();
  const [error, setError] = useState();

  const filterInvalidVintages = (fundTags) => {
    // Extract all valid vintage codes from fundFamilies
    const validVintageCodes = (Array.isArray(fundFamilies) ? fundFamilies : [])
      .map((family) => family.vintages.map((vintage) => vintage.code))
      .flat();

    // Filter the fund tags based on the valid vintage codes
    const filteredFundTags = fundTags.filter(
      (tag) => tag.nsFundCode
        && validVintageCodes.includes(tag.nsFundCode.toUpperCase()),
    );
    return filteredFundTags;
  };

  const fetchFundMinimumAmount = async (fundTags) => {
    try {
      const filteredFundTagList = filterInvalidVintages(fundTags);
      const vintagePromises = filteredFundTagList.map((tag) => getFundVintagesFromCache(tag.nsFundCode.toUpperCase()));
      const vintageResults = await Promise.all(vintagePromises);

      const firstOpenVintage = vintageResults.find(
        (vintage) => new Date(vintage?.data?.closeDate) > new Date()
          && vintage?.data?.minimumInvestmentAmount,
      );

      if (firstOpenVintage) {
        setMinimumAmount(firstOpenVintage.data.minimumInvestmentAmount);
      }
    } catch (error) {
      console.error('Error fetching fund vintages:', error);
    }
  };

  useEffect(() => {
    // Retrieve all nsCode from the FundTags associated with the fundKey in fundData.
    // Use Promise.all to find the first available fund vintage that is open and has a minimum amount set.
    // Set that value as the minimum allowable amount for the input field.
    const fundTags = fundData[fund]?.settings?.fundTags || [];
    if (fundTags.length > 0) {
      fetchFundMinimumAmount(fundTags);
    }
  }, [formData, fund, fundData]);

  const handleBlur = useCallback(
    (event) => {
      const formattedValue = event.target.value;
      const numericValue = formattedValue.replace(/[$,]/g, '');
      if (minimumAmount >= 0 && parseFloat(numericValue) < minimumAmount) {
        setError(`The amount must be greater than ${minimumAmount}`);
      } else {
        setError('');
      }
      setFormData((prevData) => {
        const updatedFormData = { ...prevData };
        if (parseFloat(numericValue) > 0) {
          updatedFormData[fund] = {
            ...prevData[fund],
            amount: numericValue,
          };
        } else {
          if (updatedFormData[fund]) {
            delete updatedFormData[fund].amount;
          }
        }
        onUpdateAllocation(updatedFormData);
        return updatedFormData;
      });
    },
    [fund, setFormData],
  );

  const handleCheckboxChange = (key) => (event) => {
    const { checked } = event.target;
    setFormData((prevData) => {
      const updatedFormData = {
        ...prevData,
        [fund]: {
          ...prevData[fund],
          [key]: checked,
        },
      };
      onUpdateAllocation(updatedFormData);
      return updatedFormData;
    });
  };

  return (
    <div>
      <Grid
        container
        className={`${classes.card} ${
          useLightBackground ? classes.lightBackground : ''
        }`}
      >
        <Grid item xs={12} lg={7} className={classes.description}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {logo && (
              <img
                src={logo}
                alt={title}
                style={{ width: '50px', height: '50px', marginRight: '10px' }}
              />
            )}
            <CardLabelTypography
              component="label"
              style={{ padding: '25px 5px' }}
            >
              {title}
            </CardLabelTypography>
          </div>
          <CardSubtitleTypography
            component="div"
            style={{ paddingTop: '10px', fontWeight: '700' }}
            dangerouslySetInnerHTML={{ __html: fundFamilysubtitle }}
          />
          <CardSubtitleTypography
            component="div"
            className={classes.fundSubtitle}
            dangerouslySetInnerHTML={{ __html: subtitle }}
          />
        </Grid>
        <Grid item xs={12} lg={5} sx={{ pl: { lg: '30px' } }}>
          <Stack mt={4} ml={2}>
            <ToggleIconWithInput
              formData={formData}
              amount={
                formData?.[fund]?.amount > 0 ? formData?.[fund]?.amount : ''
              }
              handleBlur={handleBlur}
              showRadioButton={false}
            />
            <Box textAlign="left">
              <FormControlLabel
                style={{ paddingTop: '20px' }}
                control={(
                  <StyledCheckBox
                    checked={formData?.[fund]?.isAutoAnnualReinvestmentSelected ?? true}
                    onChange={handleCheckboxChange(
                      'isAutoAnnualReinvestmentSelected',
                    )}
                  />
                )}
                label={(
                  <AllocationLableTypography>
                    {autoAnnualReinvestmentLabel
                      || 'Repeat Investment Annually?'}
                  </AllocationLableTypography>
                )}
              />
            </Box>
            <Box textAlign="left">
              <FormControlLabel
                control={(
                  <StyledCheckBox
                    checked={formData?.[fund]?.isAutoDistributionReinvestmentSelected ?? true}
                    onChange={handleCheckboxChange(
                      'isAutoDistributionReinvestmentSelected',
                    )}
                  />
                )}
                label={(
                  <AllocationLableTypography>
                    {autoDistributionReinvestmentLabel
                      || 'Auto-Reinvest Distributions?'}
                  </AllocationLableTypography>
                )}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
      {error && <div className={classes.errorMessage}>{error}</div>}
    </div>
  );
}

PerFundAllocationStrategyCard.propTypes = {
  logo: PropTypes.string,
  title: PropTypes.string,
  fundFamilysubtitle: PropTypes.string,
  subtitle: PropTypes.string,
  formData: PropTypes.object.isRequired,
  setFormData: PropTypes.func.isRequired,
  fund: PropTypes.string.isRequired,
  onUpdateAllocation: PropTypes.func.isRequired,
  fundData: PropTypes.object.isRequired,
  fundFamilies: PropTypes.arrayOf(PropTypes.object).isRequired,
  useLightBackground: PropTypes.bool,
  autoAnnualReinvestmentLabel: PropTypes.string,
  autoDistributionReinvestmentLabel: PropTypes.string,
};

PerFundAllocationStrategyCard.defaultProps = {
  logo: null,
  title: null,
  fundFamilysubtitle: null,
  subtitle: null,
  autoAnnualReinvestmentLabel: null,
  autoDistributionReinvestmentLabel: null,
  useLightBackground: false,
};
